"use client";

// src/components/organisms/legal/posthog-pageview.tsx
// The only source of `$pageview` events. `capture_pageview` is off in
// init-options.ts, so nothing but this component fires one — on the first
// load and on every App Router pathname/search-params change.
//
// `useSearchParams()` requires this component to be mounted inside a
// `<Suspense>` boundary (see app/layout.tsx), or Next.js opts the whole route
// out of static rendering.

import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useRef } from "react";
import { capturePageview } from "@/lib/posthog/client";

/** Pure: builds the URL `capturePageview` receives. `search` is
 *  `searchParams.toString()` — no leading `?` — so an empty value yields no
 *  `?` in the result. */
export function pageviewUrl(origin: string, pathname: string, search: string): string {
  return `${origin}${pathname}${search ? `?${search}` : ""}`;
}

export function PostHogPageview() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  // Guards against React strict mode's double effect invocation and a
  // re-render that leaves the URL unchanged. A real navigation back to the
  // same URL later (e.g. clicking a link to the current page) is an accepted
  // limit: it will not capture a second `$pageview`.
  const lastCapturedUrl = useRef<string | null>(null);

  useEffect(() => {
    if (!pathname) return;
    const url = pageviewUrl(window.location.origin, pathname, searchParams?.toString() ?? "");
    if (lastCapturedUrl.current === url) return;
    lastCapturedUrl.current = url;
    capturePageview(url);
  }, [pathname, searchParams]);

  return null;
}
